import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import { OrganizationRole } from "~/api/model";
import { useGetOrganization } from "~/api/organizations/organizations";
import { CalendlyWidget } from "~/components/3rd-party/CalendlyWidget";
import { DisplayFor } from "~/components/permissions/DisplayFor";
import { Button } from "~/components/ui/button";
import { CALENDLY_URL } from "~/constants/index";
import { useOrganizationParams } from "~/helpers";
import { DEFAULT_FREE_TRIAL_DAYS, DEFAULT_FREE_TRIAL_LIMIT } from "./constants";
import { UsageLimitModal } from "./UsageLimitModal";

export const PaymentRequiredBanner: React.FC = () => {
  const { organizationId } = useOrganizationParams();
  const [isUsageLimitModalOpen, setIsUsageLimitModalOpen] = useState(false);
  const { data: organizationInfo } = useGetOrganization(
    organizationId as string
  );

  const freeTrialLimit =
    organizationInfo?.freeTrialLimit || DEFAULT_FREE_TRIAL_LIMIT;
  const isLimitReached =
    (organizationInfo?.pagesUsedInThisBillingPeriod ?? 0) >= freeTrialLimit;

  const handlePayment = () => {
    setIsUsageLimitModalOpen(true);
  };

  const handleBookDemo = () => {
    const calendly = (window as any).Calendly;
    if (calendly?.initPopupWidget) {
      calendly.initPopupWidget({ url: CALENDLY_URL });
    } else {
      window.open(CALENDLY_URL, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div
      className="bg-yellow-100 dark:bg-yellow-900/50 border border-yellow-300 dark:border-yellow-700 text-yellow-700 dark:text-yellow-200 pl-6 pr-4 py-4 rounded-md flex justify-between items-center"
      role="alert"
    >
      <CalendlyWidget />
      <div className="text-foreground flex flex-col gap-1">
        <p className="font-semibold">
          <FormattedMessage
            id="payment.trialIsOver"
            defaultMessage="Your Trial Period is Over"
          />
        </p>
        <p className="text-sm">
          <FormattedMessage
            id="payment.trialIsOver.hint"
            defaultMessage="Your free trial includes {limit} free pages within a {days}-day trial period. Additional documents exceed this limit. To continue using the service, please add your payment method."
            values={{
              limit: freeTrialLimit.toLocaleString(),
              days: DEFAULT_FREE_TRIAL_DAYS,
            }}
          />
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={handleBookDemo}>
          <FormattedMessage id="payment.bookDemo" defaultMessage="Book Demo" />
        </Button>
        <DisplayFor
          roles={[OrganizationRole.OWNER, OrganizationRole.SUPERADMIN]}
        >
          <Button onClick={handlePayment}>
            <FormattedMessage
              id="payment.update"
              defaultMessage="Update Payment"
            />
          </Button>
        </DisplayFor>
      </div>
      {isUsageLimitModalOpen && organizationId && (
        <UsageLimitModal
          organizationId={organizationId}
          freeTrialLimit={freeTrialLimit}
          isLimitReached={isLimitReached}
          onClose={() => setIsUsageLimitModalOpen(false)}
        />
      )}
    </div>
  );
};
